import classnames from 'classnames';
import * as React from 'react';

import Avatar from '../components/Avatar';
import { isReadOnlyMode } from '../globals';
import { formatTime } from '../helpers/date';
import {
	ChatMessage,
	ChatMessageTypes,
	LiveChatAgent,
	LiveChatStatus,
	SenderTypes,
	useChatState,
} from '../store';
import AddToBasketMessage from './message-types/AddToBasketMessage';
import ArticlesMessage from './message-types/ArticlesMessage';
import ButtonMessage from './message-types/ButtonMessage';
import ImageMessage from './message-types/ImageMessage';
import LinkButtonMessage from './message-types/LinkButtonMessage';
import LiveChatAgentStateChangeMessage from './message-types/LiveChatAgentStateChangeMessage';
import LiveChatAgentTransferInitiateMessage from './message-types/LiveChatAgentTransferInitiateMessage';
import LiveChatAutoDisconnectNoticeMessage from './message-types/LiveChatAutoDisconnectNoticeMessage';
import LiveChatAutoDisconnectionMessage from './message-types/LiveChatAutoDisconnectionMessage';
import LiveChatExitMessage from './message-types/LiveChatExitMessage';
import PageUpdateMessage from './message-types/PageUpdateMessage';
import ProductCardMessage from './message-types/ProductCardMessage';
import RecommendedProductsMessage from './message-types/RecommendedProductsMessage';
import SimpleMessage from './message-types/SimpleMessage';
import SuggestedPhrasesMessage from './message-types/SuggestedPhrasesMessage';
import TipMessage from './message-types/TipMessage';
import TrackedDeliveryMessage from './message-types/TrackedDeliveryMessage';
import VideoMessage from './message-types/VideoMessage';

export interface ChatMessageGroupVisualCues {
	isRendered: boolean;
	hasAvatar: boolean;
	hasTimestamp: boolean;
}

export interface ChatMessageGroup {
	messages: ChatMessage[];
	sender: SenderTypes;
	timestamp: number;
	liveChatAgent?: LiveChatAgent;
	visualCues?: ChatMessageGroupVisualCues;
	intent?: string;
}

interface MessageGroupProps extends React.HTMLProps<HTMLDivElement> {
	groupData: ChatMessageGroup;
	innerRef?: React.RefObject<HTMLDivElement>;
}

export const MessageGroup: React.FC<MessageGroupProps> = ({
	groupData,
	innerRef,
	...props
}) => {
	const {
		messages,
		sender,
		timestamp,
		liveChatAgent: groupLiveChatAgent,
		visualCues,
		intent,
	} = groupData;

	// TODO: Remove once we have extended the ChatHistory type in to a view model
	// See: https://trello.com/c/DNTobJOR/302-tech-debt-refactor-history-models-interfaces-to-split-between-apiresponsemodels-and-viewmodels

	const hasAvatar =
		visualCues && visualCues.hasAvatar ? visualCues.hasAvatar : undefined;
	const hasTimestamp =
		visualCues && visualCues.hasTimestamp ? visualCues.hasTimestamp : undefined;
	/* eslint-enable no-undefined */

	const { liveChatAgent, liveChatStatus } = useChatState();

	const isSenderConnected =
		groupLiveChatAgent &&
		liveChatAgent &&
		groupLiveChatAgent.participantId === liveChatAgent.participantId
			? liveChatStatus === LiveChatStatus.Connected
			: false;

	const avatarSenderInfo = {
		...groupLiveChatAgent,
		isConnected: isSenderConnected,
	} as LiveChatAgent;

	return (
		<div data-testid="message-group" ref={innerRef} {...props}>
			{hasAvatar && <Avatar sender={sender} senderInfo={avatarSenderInfo} />}

			{messages.map((message) => {
				const key = message.id;
				switch (message.type) {
					case ChatMessageTypes.Button:
						return (
							<ButtonMessage
								key={key}
								groupIntent={intent}
								buttons={message.content}
								isReadOnly={isReadOnlyMode}
							/>
						);
					case ChatMessageTypes.RecommendedProducts:
						return (
							<RecommendedProductsMessage
								key={key}
								products={message.content}
							/>
						);
					case ChatMessageTypes.LinkButton:
						return <LinkButtonMessage key={key} button={message.content} />;
					case ChatMessageTypes.AddToBasket:
						return (
							<AddToBasketMessage
								key={key}
								groupIntent={intent}
								content={message.content}
							/>
						);
					case ChatMessageTypes.Tip:
						return <TipMessage key={key}>{message.content}</TipMessage>;
					case ChatMessageTypes.Image:
						return <ImageMessage key={key} imageData={message.content} />;
					case ChatMessageTypes.Video:
						return <VideoMessage key={key} videoData={message.content} />;
					case ChatMessageTypes.Simple:
						return (
							<SimpleMessage key={key} sender={sender} messageData={message}>
								{message.content}
							</SimpleMessage>
						);
					case ChatMessageTypes.SuggestedPhrases:
						return (
							<SuggestedPhrasesMessage
								key={key}
								groupIntent={intent}
								phrases={message.content}
								isReadOnly={isReadOnlyMode}
							/>
						);
					case ChatMessageTypes.TrackedDelivery:
						return (
							<TrackedDeliveryMessage
								key={key}
								deliveryData={message.content}
							/>
						);

					case ChatMessageTypes.ProductCard:
						return (
							<ProductCardMessage
								key={key}
								messageId={message.id}
								productData={message.content}
							/>
						);

					case ChatMessageTypes.LiveChatAgentStateChange:
						return (
							<LiveChatAgentStateChangeMessage
								key={key}
								timestamp={timestamp}
								content={message.content}
							/>
						);

					case ChatMessageTypes.LiveChatAgentTransferInitiate:
						return (
							<LiveChatAgentTransferInitiateMessage
								key={key}
								timestamp={timestamp}
							/>
						);

					case ChatMessageTypes.LiveChatAutoDisconnectNotice:
						return (
							<LiveChatAutoDisconnectNoticeMessage
								key={key}
								timestamp={timestamp}
							/>
						);

					case ChatMessageTypes.LiveChatAutoDisconnection:
						return (
							<LiveChatAutoDisconnectionMessage
								key={key}
								timestamp={timestamp}
							/>
						);

					case ChatMessageTypes.LiveChatExit:
						return <LiveChatExitMessage key={key} timestamp={timestamp} />;

					case ChatMessageTypes.Articles:
						return <ArticlesMessage key={key} articles={message.content} />;

					case ChatMessageTypes.PageUpdate:
						return isReadOnlyMode ? (
							<PageUpdateMessage key={key} pageData={message.content} />
						) : null;

					default:
						return null;
				}
			})}

			{hasTimestamp && (
				<div
					data-testid="timestamp"
					className={classnames('timestamp mx-4 text-caption mt-0', {
						'self-end': sender === SenderTypes.User,
						'self-start': sender === SenderTypes.Chatbot,
					})}
				>
					{/* {JSON.stringify(messages)} */}
					{formatTime(timestamp)}
					{groupLiveChatAgent && sender === SenderTypes.LiveChat && (
						<React.Fragment>
							&nbsp;&#8226; {groupLiveChatAgent.name.split(' ')[0]}
						</React.Fragment>
					)}
				</div>
			)}
		</div>
	);
};

export default MessageGroup;
